import React, { useState, useEffect } from 'react';
import { groupsAPI, hotseatAPI } from '../services/api';

const Hotseats = () => {
  const [groups, setGroups] = useState([]);
  const [hotseats, setHotseats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchGroups();
    fetchHotseats();
  }, []);

  useEffect(() => {
    fetchHotseats();
  }, [selectedGroup, selectedMonth]);

  const fetchGroups = async () => {
    try {
      const response = await groupsAPI.getAll();
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHotseats = async () => {
    try {
      let response;
      if (selectedGroup && selectedMonth) {
        // Get specific group and month
        response = await hotseatAPI.getByGroup(selectedGroup);
        const filtered = response.data.filter(h => h.month === selectedMonth);
        setHotseats(filtered);
      } else if (selectedGroup) {
        // Get all months for specific group
        response = await hotseatAPI.getByGroup(selectedGroup);
        setHotseats(response.data);
      } else if (selectedMonth) {
        // Get all groups for specific month
        response = await hotseatAPI.getByMonth(selectedMonth);
        setHotseats(response.data);
      } else {
        // Get all hotseats
        response = await hotseatAPI.getAll();
        setHotseats(response.data);
      }
    } catch (error) {
      console.error('Error fetching hotseats:', error);
      setHotseats([]);
    }
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getUpcomingMonths = () => {
    const months = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.push({
        value: monthStr,
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    
    return months;
  };

  const getGroupsNeedingHotseats = () => {
    if (!selectedMonth) return [];
    
    const groupsWithHotseats = hotseats.map(h => h.group_id);
    return groups.filter(group => !groupsWithHotseats.includes(group.id));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🎯 Hotseat Management</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Group
            </label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Groups</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Month
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Months</option>
              {getUpcomingMonths().map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Alert for missing hotseats */}
      {selectedMonth && getGroupsNeedingHotseats().length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Missing Hotseat Assignments
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>The following groups need hotseat assignments for {getMonthName(selectedMonth)}:</p>
                <ul className="list-disc list-inside mt-1">
                  {getGroupsNeedingHotseats().map(group => (
                    <li key={group.id}>{group.name}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hotseats List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">
            Hotseat Schedule
            {selectedGroup && ` - ${groups.find(g => g.id.toString() === selectedGroup)?.name}`}
            {selectedMonth && ` - ${getMonthName(selectedMonth)}`}
          </h2>
        </div>
        
        <div className="divide-y divide-gray-200">
          {hotseats.length > 0 ? (
            hotseats.map((hotseat, index) => (
              <div key={index} className="px-6 py-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {hotseat.group_name}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getMonthName(hotseat.month)}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {hotseat.member_names && hotseat.member_names.length > 0 ? (
                        hotseat.member_names.map((name, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                            🎯 {name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500 italic">No members assigned</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <a
                      href={`/group/${hotseat.group_id}`}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 transition-colors"
                    >
                      Manage
                    </a>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              {selectedGroup || selectedMonth ? (
                <div>
                  <p>No hotseat schedules found for the selected criteria.</p>
                  {selectedGroup && (
                    <p className="mt-2">
                      <a
                        href={`/group/${selectedGroup}`}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Go to group page to add hotseat schedules
                      </a>
                    </p>
                  )}
                </div>
              ) : (
                <p>No hotseat schedules have been created yet.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {groups.map(group => (
            <a
              key={group.id}
              href={`/group/${group.id}`}
              className="block bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow"
            >
              <h4 className="font-medium text-gray-900">{group.name}</h4>
              <p className="text-sm text-gray-600 mt-1">Manage hotseats</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Hotseats;